import React from 'react';
import Dialog, { DialogProps } from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Icon from '@mui/material/Icon';
import Typography from '@mui/material/Typography';
import Slide from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';
import Box from '@mui/material/Box';
import { GenericDialogV2Provider, useGenericDialogV2 } from './context';
import { useAppContext } from '@/context/app';
import { theme } from '@/theme/default';
import StyledLoadingButton from '@/components/common/StyledLoadingButton';
import { Grid, Stack } from '@mui/material';

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

type GenericDialogProps = Omit<
    DialogProps,
    'classes' | 'TransitionComponent' | 'fullScreen'
> & {
    formId?: string;
    title: string;
    titleIcon?: React.ReactNode;
    subTitle?: string;
    titleColor?: string;
    okText?: string;
    cancelText?: string;
    okIcon?: React.ReactNode;
    cancelIcon?: React.ReactNode;
    loading?: boolean;
    hideCancel?: boolean;
    hideOk?: boolean;
    hideHeader?: boolean;
    hideActions?: boolean;
    onOk?: () => void;
    onCancel?: () => void;
};

const GenericDialogV2: React.FC<GenericDialogProps> = ({
    children,
    title,
    titleIcon,
    subTitle = '',
    okText,
    cancelText,
    formId,
    fullWidth = true,
    onClose,
    maxWidth = 'sm',
    loading = false,
    hideCancel = false,
    hideOk = false,
    hideHeader = false,
    hideActions = false,
    onOk,
    onCancel,
    okIcon,
    cancelIcon,
    titleColor = theme.palette.text.primary,
    ...props
}) => {
    const { ctxLoading } = useGenericDialogV2();
    const {
        layout: { device },
    } = useAppContext();
    const handleClose = () => {
        if (onClose) {
            onClose({}, 'backdropClick');
        }
    };

    return (
        <Dialog
            fullWidth={fullWidth}
            fullScreen={device === 'mobile'}
            maxWidth={maxWidth}
            onClose={onClose}
            TransitionComponent={Transition}
            sx={{ scrollbarGutter: 'stable both-edges' }}
            {...props}
        >
            {!hideHeader && (
                <DialogTitle
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        backgroundColor: 'secondary.100',
                        marginBottom: '20px'
                    }}
                >
                    <Stack>
                        <Typography
                            variant='h6'
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                textAlign: 'left',
                                width: '100%',
                                color: titleColor,
                            }}
                        >
                            {title}
                            {titleIcon && titleIcon}
                        </Typography>
                        <Typography
                            variant='caption'
                            sx={{
                                whiteSpace: 'pre-line',
                                textAlign: 'left',
                                width: '100%',
                            }}
                        >
                            {subTitle}
                        </Typography>
                    </Stack>
                    <IconButton size="small" onClick={handleClose}>
                        <Icon>close</Icon>
                    </IconButton>
                </DialogTitle>
            )}

            <DialogContent>
                <Box>{children}</Box>
            </DialogContent>
            {
                !hideActions && (
                    <DialogActions
                        sx={{
                            display: 'flex',
                            alignItems: 'inherit',
                            gap: 1,
                            justifyContent: 'flex-end',
                            p: hideCancel && hideOk ? 0 : 2,
                        }}
                    >
                        {!hideCancel && (
                            <StyledLoadingButton
                                variant="outlined"
                                sx={{ color: 'primary.dark' }}
                                onClick={onCancel ? onCancel : handleClose}
                            >
                                <Grid container gap={1}>
                                    {cancelIcon && cancelIcon}
                                    {cancelText ?? 'Cancelar'}
                                </Grid>
                            </StyledLoadingButton>
                        )}

                        {!hideOk && (
                            <StyledLoadingButton
                                id="okBtn"
                                form={formId}
                                type={formId ? 'submit' : 'button'}
                                variant={'contained'}
                                loadingPosition={'center'}
                                loading={ctxLoading || loading}
                                onClick={formId ? undefined : onOk}
                            >
                                <Grid container gap={1}>
                                    {okIcon && okIcon}
                                    {okText ?? 'Ok'}
                                </Grid>
                            </StyledLoadingButton>
                        )}
                    </DialogActions>
                )
            }
        </Dialog>
    );
};

const GenericDialogV2WithProvider: React.FC<GenericDialogProps> = (props) => (
    <GenericDialogV2Provider>
        <GenericDialogV2 {...props} />
    </GenericDialogV2Provider>
);

export default GenericDialogV2WithProvider;
